import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  PlusCircle,
  HelpCircle,
  Keyboard,
  Moon,
  Sun,
  Globe,
  LogOut,
  FileSearch,
  Loader2,
  CornerDownLeft,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useDarkMode } from "../hooks/useDarkMode";
import { navItemsFor } from "../lib/nav";
import { matchScore } from "../lib/textMatch";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
} from "./ui/command";
import RiskBadge from "./RiskBadge";

const API = "/api";
const SEARCH_DELAY_MS = 250;
const SEARCH_LIMIT = 8;

function partyNames(txn) {
  const names = [...(txn.sellers || []), ...(txn.buyers || [])]
    .map((p) => (p && typeof p.name === "string" ? p.name.trim() : ""))
    .filter(Boolean);
  if (names.length <= 2) return names.join(", ");
  return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
}

/**
 * ⌘K: one box to open any page, run a common action or find a screening by
 * reference or party name. Pages and actions match on their names, their
 * one-line descriptions and extra keywords, in either language, ignoring
 * case and accents; screenings come from the server's register search.
 */
const CommandPalette = ({ open, onOpenChange, onOpenHelp, onOpenShortcuts }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [dark, setDark] = useDarkMode();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const requestId = useRef(0);

  // A fresh box each time it opens.
  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
      setSearching(false);
    }
  }, [open]);

  // Screenings: searched on the server once the typing pauses; an answer to
  // an older query is dropped.
  const trimmed = query.trim();
  useEffect(() => {
    if (!open || trimmed.length < 2) {
      requestId.current += 1;
      setResults([]);
      setSearching(false);
      return undefined;
    }
    const id = ++requestId.current;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/transactions`, { params: { q: trimmed, limit: SEARCH_LIMIT } });
        if (id !== requestId.current) return;
        setResults(Array.isArray(res?.data?.transactions) ? res.data.transactions : []);
      } catch {
        if (id === requestId.current) setResults([]);
      } finally {
        if (id === requestId.current) setSearching(false);
      }
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [trimmed, open]);

  const run = (fn) => {
    onOpenChange(false);
    fn();
  };

  const pages = useMemo(() => {
    const list = navItemsFor(user).map((item) => ({
      ...item,
      label: t(item.labelKey),
      description: item.helpKey ? t(item.helpKey) : "",
      section: t(item.sectionLabelKey),
    }));
    if (!trimmed) return list;
    return list
      .map((p) => ({ p, score: matchScore(trimmed, [p.label, p.keywords || "", p.section, p.description]) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((x) => x.p);
  }, [user, t, trimmed]);

  const actions = useMemo(() => {
    const list = [
      { id: "new", label: t("palette.actions.newScreening"), icon: PlusCircle, keys: "N", keywords: "new create add nouveau créer screening", run: () => navigate("/transactions/new") },
      { id: "help", label: t("palette.actions.help"), icon: HelpCircle, keys: "", keywords: "help aide faq guide", run: onOpenHelp },
      { id: "shortcuts", label: t("palette.actions.shortcuts"), icon: Keyboard, keys: "?", keywords: "keyboard clavier raccourcis shortcuts", run: onOpenShortcuts },
      { id: "theme", label: dark ? t("nav.lightMode") : t("nav.darkMode"), icon: dark ? Sun : Moon, keys: "", keywords: "theme dark light sombre clair mode", run: () => setDark((d) => !d) },
      { id: "language", label: i18n.language === "fr" ? "Switch to English" : "Passer en français", icon: Globe, keys: "", keywords: "language langue english français french anglais", run: () => i18n.changeLanguage(i18n.language === "fr" ? "en" : "fr") },
      { id: "logout", label: t("nav.signOut"), icon: LogOut, keys: "", keywords: "logout sign out déconnexion quitter", run: () => { logout(); navigate("/login"); } },
    ];
    if (!trimmed) return list;
    return list
      .map((a) => ({ a, score: matchScore(trimmed, [a.label, a.keywords]) }))
      .filter((x) => x.score > 0)
      .sort((x, y) => y.score - x.score)
      .map((x) => x.a);
  }, [t, i18n, dark, setDark, navigate, logout, onOpenHelp, onOpenShortcuts, trimmed]);

  const nothing = !searching && results.length === 0 && pages.length === 0 && actions.length === 0;

  const screeningGroup = trimmed.length >= 2 && (searching || results.length > 0) && (
    <CommandGroup heading={t("palette.groups.screenings")} key="screenings">
      {searching && results.length === 0 && (
        <div className="flex items-center gap-2 px-2 py-3 text-sm text-muted-foreground" data-testid="palette-searching">
          <Loader2 className="w-4 h-4 animate-spin" /> {t("palette.searching")}
        </div>
      )}
      {results.map((txn) => (
        <CommandItem
          key={txn.id}
          value={`txn:${txn.id}`}
          onSelect={() => run(() => navigate(`/transactions/${txn.id}`))}
          className="min-h-[48px]"
          data-testid={`palette-txn-${txn.id}`}
        >
          <FileSearch className="text-cyan-600" />
          <div className="flex-1 min-w-0">
            <p className="font-medium font-mono text-[13px] truncate">{txn.reference || txn.id.slice(0, 8)}</p>
            <p className="text-xs text-muted-foreground truncate">
              {[partyNames(txn), t(`transactions.status.${txn.screening_status}`, { defaultValue: txn.screening_status || "" })]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>
          {txn.overall_risk && txn.screening_status === "completed" && <RiskBadge risk={txn.overall_risk} />}
        </CommandItem>
      ))}
    </CommandGroup>
  );

  const pageGroup = pages.length > 0 && (
    <CommandGroup heading={t("palette.groups.pages")} key="pages">
      {pages.map((p) => (
        <CommandItem
          key={p.to}
          value={`page:${p.to}`}
          onSelect={() => run(() => navigate(p.to))}
          className="min-h-[48px]"
          data-testid={`palette-page-${p.to.slice(1).replace(/\//g, "-")}`}
        >
          <p.icon className="text-slate-500" />
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">
              {p.label}
              <span className="ml-2 text-xs font-normal text-muted-foreground">{p.section}</span>
            </p>
            {p.description && <p className="text-xs text-muted-foreground truncate">{p.description}</p>}
          </div>
        </CommandItem>
      ))}
    </CommandGroup>
  );

  const actionGroup = actions.length > 0 && (
    <CommandGroup heading={t("palette.groups.actions")} key="actions">
      {actions.map((a) => (
        <CommandItem
          key={a.id}
          value={`action:${a.id}`}
          onSelect={() => run(a.run)}
          className="min-h-[44px]"
          data-testid={`palette-action-${a.id}`}
        >
          <a.icon className="text-slate-500" />
          <span className="flex-1 truncate">{a.label}</span>
          {a.keys && <CommandShortcut className="hidden md:inline">{a.keys}</CommandShortcut>}
        </CommandItem>
      ))}
    </CommandGroup>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="overflow-hidden p-0 gap-0 top-3 sm:top-[12vh] translate-y-0 data-[state=open]:slide-in-from-top-2 data-[state=closed]:slide-out-to-top-2 w-[calc(100vw-1.5rem)] max-w-xl rounded-xl sm:rounded-xl"
        data-testid="command-palette"
      >
        <DialogTitle className="sr-only">{t("palette.title")}</DialogTitle>
        <DialogDescription className="sr-only">{t("palette.description")}</DialogDescription>
        <Command
          shouldFilter={false}
          loop
          className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-14 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-2 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5"
        >
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder={t("palette.placeholder")}
            className="pr-8 text-base md:text-sm"
            data-testid="palette-input"
          />
          <CommandList className="max-h-[min(60vh,480px)] pb-1">
            {nothing && <CommandEmpty>{t("palette.noResults")}</CommandEmpty>}
            {/* With a query, screenings lead; without one, actions do. */}
            {trimmed ? [screeningGroup, pageGroup, actionGroup] : [actionGroup, pageGroup]}
          </CommandList>
          <div className="hidden md:flex items-center gap-4 px-4 py-2 border-t text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1"><kbd className="kbd">↑</kbd><kbd className="kbd">↓</kbd> {t("palette.hintMove")}</span>
            <span className="flex items-center gap-1"><kbd className="kbd"><CornerDownLeft className="w-3 h-3" /></kbd> {t("palette.hintOpen")}</span>
            <span className="flex items-center gap-1"><kbd className="kbd">Esc</kbd> {t("palette.hintClose")}</span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
};

export default CommandPalette;
